import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Plus } from 'lucide-react';
import {
  getLaneOperatingHours,
  bulkCreateLaneOperatingHours,
  updateLaneOperatingHour,
  deleteLaneOperatingHour
} from '../../../services/laneOperatingHoursService';
import terminalService from '../../../services/terminalService';
import checkpointService from '../../../services/checkpointService';
import laneTypeService from '../../../services/laneTypeService';
import { LaneOperatingHoursEditor } from '../LaneOperatingHoursEditor';
import { CalendarGrid } from './CalendarGrid';
import { MobileCards } from './MobileCards';
import { permissionChecks } from '../../../lib/auth/helpers';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Sunday', short: 'Sun' },
  { value: 2, label: 'Monday', short: 'Mon' },
  { value: 3, label: 'Tuesday', short: 'Tue' },
  { value: 4, label: 'Wednesday', short: 'Wed' },
  { value: 5, label: 'Thursday', short: 'Thu' },
  { value: 6, label: 'Friday', short: 'Fri' },
  { value: 7, label: 'Saturday', short: 'Sat' }
];

export const LaneOperatingHoursCalendar: React.FC = () => {
  // Permission checks
  const canUpdate = permissionChecks.canUpdateCalendar();
  
  const [loading, setLoading] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Data
  const [terminals, setTerminals] = useState<any[]>([]);
  const [allCheckpoints, setAllCheckpoints] = useState<any[]>([]);
  const [allLaneTypes, setAllLaneTypes] = useState<any[]>([]);
  const [operatingHours, setOperatingHours] = useState<any[]>([]);
  
  // Filters
  const [selectedTerminalId, setSelectedTerminalId] = useState<number | null>(null);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<number | null>(null);
  const [selectedDays, setSelectedDays] = useState<number[]>([2]); // Default to Monday for mobile
  
  // Modal for bulk create
  const [showEditor, setShowEditor] = useState(false);
  const [addContext, setAddContext] = useState<any>(null);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);
      // When switching to mobile, ensure only one day is selected
      if (newIsMobile && selectedDays.length !== 1) {
        setSelectedDays([selectedDays[0] || 2]); // Keep first selected or default to Monday
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedDays]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadOperatingHours();
  }, [selectedTerminalId, selectedCheckpointId, selectedDays]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [terminalsData, checkpointsData, laneTypesData] = await Promise.all([
        terminalService.getTerminals(),
        checkpointService.getCheckpoints(),
        laneTypeService.getAllLaneTypes()
      ]);
      
      setTerminals(terminalsData || []);
      setAllCheckpoints(checkpointsData || []);
      setAllLaneTypes(laneTypesData || []);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadOperatingHours = async () => {
    try {
      const params: any = {};
      if (selectedTerminalId) params.terminal_id = selectedTerminalId;
      if (selectedCheckpointId) params.checkpoint_id = selectedCheckpointId;
      
      const response = await getLaneOperatingHours(params);
      
      if (response.success && response.data) {
        setOperatingHours(response.data.operating_hours || []);
      }
    } catch (error) {
      console.error('Failed to load operating hours:', error);
    }
  };

  const handleTerminalChange = (terminalId: string) => {
    const id = terminalId === 'all' ? null : parseInt(terminalId);
    setSelectedTerminalId(id);
    setSelectedCheckpointId(null); // Reset checkpoint when terminal changes
  };

  const handleCheckpointChange = (checkpointId: string) => {
    const id = checkpointId === 'all' ? null : parseInt(checkpointId);
    setSelectedCheckpointId(id);
  };

  const toggleDay = (dayValue: number) => {
    if (isMobile) {
      // Mobile: Single selection only
      setSelectedDays([dayValue]);
    } else {
      // Desktop: Multi-select
      setSelectedDays(prev => 
        prev.includes(dayValue)
          ? prev.filter(d => d !== dayValue)
          : [...prev, dayValue].sort((a, b) => a - b)
      );
    }
  };

  const handleAddHours = (context: { checkpoint_id: number; checkpoint_name: string; day_of_week: number; lanetype_id: number }) => {
    setAddContext(context);
    setShowEditor(true);
  };

  const handleUpdateHour = async (hourId: number, startTime: string, endTime: string) => {
    setSavingHours(true);
    try {
      await updateLaneOperatingHour(hourId, {
        start_time: startTime,
        end_time: endTime
      });
      toast.success('Operating hour updated');
      await loadOperatingHours();
    } catch (error: any) {
      console.error('Failed to update:', error);
      toast.error(error.response?.data?.message || 'Failed to update operating hour');
      throw error; // Re-throw to let TimeSlotCell know it failed
    } finally {
      setSavingHours(false);
    }
  };

  const handleDeleteHour = async (hourId: number) => {
    if (!window.confirm('Are you sure you want to delete this operating hour?')) return;
    
    setSavingHours(true);
    try {
      await deleteLaneOperatingHour(hourId);
      toast.success('Operating hour deleted');
      await loadOperatingHours();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete operating hour');
    } finally {
      setSavingHours(false);
    }
  };

  const handleSave = async (data: any) => {
    setSavingHours(true);
    try {
      await bulkCreateLaneOperatingHours(data);
      toast.success(`Created ${data.lanetype_ids.length} operating hours`);
      setShowEditor(false);
      await loadOperatingHours();
    } catch (error: any) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.message || 'Failed to save');
    } finally {
      setSavingHours(false);
    }
  };

  // Get filtered checkpoints based on selected terminal
  const filteredCheckpoints = selectedTerminalId
    ? allCheckpoints.filter(cp => cp.terminal_id === selectedTerminalId)
    : allCheckpoints;

  // Get filtered lane types based on selected checkpoint
  const filteredLaneTypes = allLaneTypes.filter(lt => {
    if (selectedCheckpointId) {
      return lt.checkpoint_id === selectedCheckpointId && lt.purpose === 'APP' && lt.is_active;
    }
    if (selectedTerminalId) {
      const terminalCheckpointIds = filteredCheckpoints.map(cp => cp.checkpoint_id);
      return terminalCheckpointIds.includes(lt.checkpoint_id) && lt.purpose === 'APP' && lt.is_active;
    }
    return lt.purpose === 'APP' && lt.is_active;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay */}
      {savingHours && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <div className="text-white font-medium">Saving changes...</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Lane Operating Hours</h1>
          <p className="text-gray-400 mt-1">Manage operating schedules for lane types</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Terminal Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Terminal
            </label>
            <select
              value={selectedTerminalId || 'all'}
              onChange={(e) => handleTerminalChange(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Terminals</option>
              {terminals.map(terminal => (
                <option key={terminal.terminal_id} value={terminal.terminal_id}>
                  {terminal.terminal_name}
                </option>
              ))}
            </select>
          </div>

          {/* Checkpoint Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Checkpoint
            </label>
            <select
              value={selectedCheckpointId || 'all'}
              onChange={(e) => handleCheckpointChange(e.target.value)}
              disabled={!selectedTerminalId}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="all">All Checkpoints</option>
              {filteredCheckpoints.map(checkpoint => (
                <option key={checkpoint.checkpoint_id} value={checkpoint.checkpoint_id}>
                  {checkpoint.checkpoint_name}
                </option>
              ))}
            </select>
          </div>

          {/* Days Filter */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                Days
              </label>
              {/* Select All / Deselect All - Desktop Only */}
              {!isMobile && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedDays([1, 2, 3, 4, 5, 6, 7])}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Select All
                  </button>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={() => setSelectedDays([])}
                    className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    Deselect All
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map(day => (
                <button
                  key={day.value}
                  onClick={() => toggleDay(day.value)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    selectedDays.includes(day.value)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {day.short}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid or Mobile Cards */}
      {isMobile ? (
        <MobileCards
          operatingHours={operatingHours}
          laneTypes={filteredLaneTypes}
          checkpoints={filteredCheckpoints}
          terminals={terminals}
          selectedDays={selectedDays}
          onUpdate={handleUpdateHour}
          onDelete={handleDeleteHour}
          onAdd={handleAddHours}
          canUpdate={canUpdate}
        />
      ) : (
        <CalendarGrid
          operatingHours={operatingHours}
          laneTypes={filteredLaneTypes}
          checkpoints={filteredCheckpoints}
          terminals={terminals}
          selectedDays={selectedDays}
          onUpdate={handleUpdateHour}
          onDelete={handleDeleteHour}
          onAdd={handleAddHours}
          canUpdate={canUpdate}
        />
      )}

      {/* Editor Modal - Only for bulk create */}
      {showEditor && addContext && (
        <LaneOperatingHoursEditor
          context={addContext}
          availableLaneTypes={filteredLaneTypes}
          existingHours={operatingHours}
          onSave={handleSave}
          onCancel={() => {
            setShowEditor(false);
            setAddContext(null);
          }}
          saving={savingHours}
        />
      )}
    </div>
  );
};